import { Injectable } from '@angular/core';
import { HttpService } from './http.service';
import {
  map, Observable,
  ComparisonReportClubProgramDataWithPayment, ComparisonReportSession, ComparisonReport, ScholarshipReport, CreditReport, CCDeclinedReport,
  DiscountReport, CCExpiredReport, InCompleteReport, RefundReport, PayoutReport, TeamRegistrationReport, FinancialsReport, UserScholarshipReport, RemovedBallanceReport
} from '../models';
import { DisputeReportUserListData, DisputeSubmitData } from '../models/reports/dispute-report';
import { HttpHeaders } from '@angular/common/http';

@Injectable()
export class ReportsService {

  private _url = '/api/report';
  private _urlClubProgram = '/api/clubProgram';

  constructor(private httpService: HttpService) { }

  // -------------- Scholarship Report -------------- //
  getScholerReport(id: string): Observable<ScholarshipReport> {
    return this.httpService.get(this._url + '/get-ScholarshipReportData/' + id).pipe(map(res => new ScholarshipReport(res)));
  }

  // -------------- Scholarship Report New -------------- //
  getScholarshipReportData(currentProgramId: number): Observable<UserScholarshipReport> {
    return this.httpService.get(this._url + '/get-ScholarshipReportDataNew/' + currentProgramId).pipe(map(
      res => new UserScholarshipReport(res))
    );
  }
  
  // -------------- Dispute Report -------------- //
  getDisputeReportData(currentProgramId: string): Observable<DisputeReportUserListData> {
    return this.httpService.get(this._urlClubProgram + '/get-stripe-dispute-report/' + currentProgramId).pipe(map(
      res => new DisputeReportUserListData(res))
    );
  }
  
  // -------------- Submit Dispute -------------- //
  submitDispute(payload: DisputeSubmitData){
    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
        'responseType': 'text'
      })
    };
    return this.httpService.post(this._url + '/submit-new-dispute', payload, httpOptions);
  }

  // -------------- Comparison Report -------------- //
  getComparisonReportCurrentProgramData(id: number): Observable<ComparisonReportClubProgramDataWithPayment> {
    return this.httpService.get(this._url + '/get-ComparisonReportCurrentProgramData/' + id).pipe(map(
      res => new ComparisonReportClubProgramDataWithPayment(res))
    );
  }

  getComparisonReportSessions(clubId: number, clubProgramTypeID: number): Observable<ComparisonReportSession[]> {
    return this.httpService.get(`${this._url}/get-ClubProgramDropDownList/${clubId}/${clubProgramTypeID}`).pipe(map(
      res => res.map(session => new ComparisonReportSession(session)))
    );
  }

  getComparisonReport(currentProgramId: number, selectedProgramID: number): Observable<ComparisonReport> {
    return this.httpService.get(`${this._url}/get-ComparisonReportDataByID/${currentProgramId}/${selectedProgramID}`).pipe(map(
      report => new ComparisonReport(report))
    );
  }

  // -------------- Credit Report -------------- //
  getCreditReportData(currentProgramId: string): Observable<CreditReport> {
    return this.httpService.get(this._url + '/get-CreditReportData/' + currentProgramId).pipe(map(
      res => new CreditReport(res))
    );
  }

  // -------------- Declined Credit Report -------------- //
  getDeclinedCreditCardReportData(currentProgramId: string): Observable<CCDeclinedReport> {
    return this.httpService.get(this._url + '/get-CreditCardDeclinedReportData/' + currentProgramId).pipe(map(
      res => new CCDeclinedReport(res))
    );
  }

  // -------------- Discount Credit Report -------------- //
  getDiscountReportData(currentProgramId: string | number): Observable<DiscountReport> {
    return this.httpService.get(this._url + '/get-DiscountReportData/' + currentProgramId).pipe(map(
      res => new DiscountReport(res))
    );
  }

  // -------------- Expired Credit Card Report -------------- //
  getExpiredCreditCardReportData(currentProgramId: string): Observable<CCExpiredReport> {
    return this.httpService.get(this._url + '/get-CreditCardExpireReportData/' + currentProgramId).pipe(map(
      res => new CCExpiredReport(res))
    );
  }

  // -------------- InComplete Report Report -------------- //
  getInCompleteReportReportData(currentProgramId: string): Observable<InCompleteReport> {
    return this.httpService.get(this._url + '/get-IncompleteReportData/' + currentProgramId).pipe(map(
      res => new InCompleteReport(res))
    );
  }

  deleteIncompletedUsers(cupfIDs: number[]) {
    return this.httpService.post(this._url + '/delete-ClubUserProgramFeesByID', { IDs: cupfIDs });
  }

  // -------------- Refund Report Report -------------- //
  getRefundReportData(currentProgramId: string): Observable<RefundReport> {
    return this.httpService.get(this._url + '/get-RefundReportData/' + currentProgramId).pipe(map(
      res => new RefundReport(res))
    );
  }

  // -------------- Payout Report Report -------------- //
  getPayoutReportData(currentProgramId: string): Observable<PayoutReport> {
    return this.httpService.get(this._url + '/get-PayoutReportData/' + currentProgramId).pipe(map(
      res => new PayoutReport(res))
    );
  }

  // -------------- Team Registration Report -------------- //
  getTeamRegistrationReportData(currentProgramId: string): Observable<TeamRegistrationReport> {
    return this.httpService.get(this._url + '/get-TeamRegistrationReportData/' + currentProgramId).pipe(map(
      res => new TeamRegistrationReport(res))
    );
  }

  // -------------- Financials Report -------------- //
  getFinancialsReportData(currentProgramId: string): Observable<FinancialsReport> {
    return this.httpService.get(this._url + '/get-FinancialsReportData/' + currentProgramId).pipe(map(
      res => new FinancialsReport(res))
    );
  }

  // -------------- Financials Report -------------- //
  getRemovedBallancesReportData(currentProgramId: string): Observable<RemovedBallanceReport> {
    return this.httpService.get(this._url + '/get-RemovedBallancesReportData/' + currentProgramId).pipe(map(
      res => new RemovedBallanceReport(res))
    );
  }

  // -------------- Financials Report -------------- //
  getPastDueReportData(currentProgramId: string): Observable<any> {
    return this.httpService.get(this._url + '/get-PastDueReportData/' + currentProgramId);
  }

  // ------------- Reconciliation Report ------------- //
  getReconciliationReportData(currentProgramId: string | number): Observable<any> {
    return this.httpService.get(this._url + '/get-ReconciliationReport/' + currentProgramId).pipe(map(
      res => ({
        list: res.list.map(r => ({
          ...r,
          paidDate: new Date(r.paidDate).toISOString().slice(0, 10),
          arrivalDate: new Date(r.arrivalDate).toISOString().slice(0, 10),
        }))
      }))
    );
  }

  getReconciliationBankReportData(bank: string): Observable<any> {
    return this.httpService.get(this._url + '/get-BankReconciliationReport/' + bank).pipe(map(
      res => ({
        list: res.list.map(r => ({
          ...r,
          paidDate: new Date(r.paidDate).toISOString().slice(0, 10),
          arrivalDate: new Date(r.arrivalDate).toISOString().slice(0, 10),
        }))
      }))
    );
  }

  getReconciliationBankReportDataNew(bank: string): Observable<any> {
    return this.httpService.get(this._url + '/get-BankReconciliationReportNew/' + bank).pipe(map(
      res => ({ list: res.list }))
    );
  }

  // ------------- Donation Report ------------- //
  getDonationReportData(currentProgramId: string | number): Observable<any> {
    return this.httpService.get(this._url + '/get-DonationReport/' + currentProgramId);
  }

  // ------------- Sponsorship Report ------------- //
  getSponsorshipReportData(currentProgramId: string | number): Observable<any> {
    return this.httpService.get(this._url + '/get-SponsorshipReport/' + currentProgramId);
  }

}



// WEBPACK FOOTER //
// ./src/app/services/reports.service.ts